import React from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthProvider';
import Loading from '../../components/Loading';
import {
  GetDocuSignEnvelopeSignedStatusByInvoiceOrGroupIdQuery,
  useGetDocuSignEnvelopeSignedStatusByInvoiceOrGroupIdLazyQuery
} from '../../generated/graphql';
import { useLazyQueryWithRetry } from '../../utils/hooks/useLazyQueryWithRetry';
import CustomerAgreement from './CustomerAgreement';
import TnCSignedFlow, { ExternalRedirect } from './TnCSignedFlow';

interface CompleteParams {
  invoiceIdOrGroupId: string;
}

/**
 * Group-level answer from getDocuSignEnvelopeSignedStatusByInvoiceOrGroupId
 * (NCP-5455, NCP-5461), forwarded as a JSONObject so every field is optional
 * on the wire.
 *
 * - `shouldSendPolicyDocuments` mirrors GroupPaymentView.shouldSendPolicyDocuments:
 *   true unless the group's DocuSign envelope status is an explicit `false`
 *   (absent / null = never initiated, treated as not-unsigned). Products that
 *   disagree are reconciled backend-side (first product wins).
 * - `isFullyPaid`: every product Paid with a Policy. `false` while the
 *   payment_intent.succeeded webhook is still settling the group, or when the
 *   customer never completed checkout.
 * - `stripeCheckoutSessionRedirectUrl`: a live retry Checkout Session the
 *   backend opened after a sibling's charge failed.
 * - `transactionType`: the group's Application transactionType.
 */
interface EnvelopeSignedStatus {
  shouldSendPolicyDocuments?: boolean;
  stripeCheckoutSessionRedirectUrl?: string | null;
  isFullyPaid?: boolean;
  transactionType?: string | null;
}

const NEW_BUSINESS = 'New Business';

// Polling cadence while a New Business group is still being settled by the
// webhook: first request on mount, then 6s, ~12-18s, 24s — four requests over
// roughly 45s, which covers the webhook's mint + Policy + retry-session work.
// Unlike TnCSignedFlow there is no NCP-1898 delay before the first request:
// every landing (any transactionType) goes through this query, and only the
// New Business not-yet-settled answer needs waiting on.
const INITIAL_DELAY = 0; // ms
const BASE_DELAY = 6000; // ms
const MAX_DELAY = 24000; // ms
const MAX_RETRIES = 4;

/**
 * The query returns a JSONObject scalar, so the payload's shape is a runtime
 * contract, not a schema one, and nothing in codegen or CI catches a change to
 * it. 6.9.0's backend (NCP-5455) answers with the flat group-level object
 * described above; every earlier backend answers with a per-LOB map,
 * `{ GL: { isDocuSignEnvelopeSigned }, EO: { ... } }`. Reading both keeps
 * `/complete` working while the frontend and backend deploys are not yet in
 * step, instead of telling every customer we "couldn't determine" the status
 * (NCP-5471).
 *
 * The legacy fold mirrors the pre-6.9.0 rule: the group is unsigned only when
 * every product's envelope is an explicit `false` (absent / null = never
 * initiated, treated as not-unsigned). It leaves `isFullyPaid` and
 * `transactionType` undefined, which is exactly how the polling and
 * settle branches below already treat a backend that predates those fields.
 */
export const normalizeEnvelopeStatus = (
  raw: unknown
): EnvelopeSignedStatus | null => {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  if (typeof obj.shouldSendPolicyDocuments === 'boolean') {
    return {
      shouldSendPolicyDocuments: obj.shouldSendPolicyDocuments,
      stripeCheckoutSessionRedirectUrl:
        typeof obj.stripeCheckoutSessionRedirectUrl === 'string'
          ? obj.stripeCheckoutSessionRedirectUrl
          : null,
      isFullyPaid:
        typeof obj.isFullyPaid === 'boolean' ? obj.isFullyPaid : undefined,
      transactionType:
        typeof obj.transactionType === 'string' ? obj.transactionType : null
    };
  }

  const perLob = Object.values(obj).filter(
    (v): v is { isDocuSignEnvelopeSigned?: boolean | null } =>
      !!v && typeof v === 'object' && 'isDocuSignEnvelopeSigned' in v
  );
  if (perLob.length === 0) return null;

  const allUnsigned = perLob.every(
    (status) => status.isDocuSignEnvelopeSigned === false
  );
  return { shouldSendPolicyDocuments: !allUnsigned };
};

const readStatus = (
  data: GetDocuSignEnvelopeSignedStatusByInvoiceOrGroupIdQuery | undefined
): EnvelopeSignedStatus | null =>
  normalizeEnvelopeStatus(
    data?.getDocuSignEnvelopeSignedStatusByInvoiceOrGroupId
  );

/**
 * New Business, not fully paid, and no retry session to send the customer to:
 * the webhook is still working, so the answer is not final yet (NCP-5461).
 *
 * The race: Stripe redirects the browser here as soon as the Checkout Session
 * completes, while the payment_intent.succeeded webhook is still settling the
 * charged product, minting the siblings' off-session charges and — if one is
 * declined — opening the retry session. Until that last write lands, the
 * group reads as unpaid with nowhere to redirect to. Every other
 * transactionType lands here with its money already settled (waive, refund,
 * cancellation, reserved renewal), so only New Business waits.
 *
 * `isFullyPaid === false` on purpose: a backend that predates the field
 * (undefined) falls through to the previous behaviour.
 */
const isAwaitingWebhook = (
  status: EnvelopeSignedStatus | null | undefined
): boolean =>
  status?.transactionType === NEW_BUSINESS &&
  status.isFullyPaid === false &&
  !status.stripeCheckoutSessionRedirectUrl;

const Complete: React.FC = (props) => {
  const { isAuthenticated: isBrokerFlow } = useAuth();
  const propsObj = JSON.parse(JSON.stringify(props));
  const state = propsObj['history']?.['location']?.['state'];

  const { invoiceIdOrGroupId } = useParams<CompleteParams>();
  const variables = { invoiceOrGroupId: invoiceIdOrGroupId };

  const lazyQuery = useGetDocuSignEnvelopeSignedStatusByInvoiceOrGroupIdLazyQuery(
    { variables }
  );

  const {
    data: docusignData,
    error: getDocusignDataError,
    loading: getDocusignDataLoading,
    called,
    reachMaxRetries
  } = useLazyQueryWithRetry({
    lazyQuery,
    variables,
    isResolved: (data) => !isAwaitingWebhook(readStatus(data)),
    initialDelay: INITIAL_DELAY,
    baseDelay: BASE_DELAY,
    maxDelay: MAX_DELAY,
    maxRetries: MAX_RETRIES
  });

  if (getDocusignDataError) {
    return (
      <div data-testid="error-msg" className="m-auto text-lg text-center">
        {"Sorry, there's an error while getting the Terms & Conditions status."}
      </div>
    );
  }

  if (getDocusignDataLoading || !called || !docusignData) {
    return <Loading />;
  }

  const envelopeStatus = readStatus(docusignData);

  if (typeof envelopeStatus?.shouldSendPolicyDocuments !== 'boolean') {
    return (
      <div data-testid="error-msg" className="m-auto text-lg text-center">
        {"Sorry, we couldn't determine the Terms & Conditions signing status."}
      </div>
    );
  }

  // Business rule: the unsigned-T&C branch below may only render once every
  // product is paid, so a New Business group that still owes money is decided
  // here first.
  if (
    envelopeStatus.transactionType === NEW_BUSINESS &&
    envelopeStatus.isFullyPaid === false
  ) {
    // Partially purchased: the backend opened a Checkout Session for this
    // group after one product's charge failed while a sibling's succeeded,
    // and it is waiting for the customer. Send them there before asking
    // anything about the T&C — the same handling TnCSignedFlow applies to
    // getPaymentStatus's `stripeCheckoutSessionRedirectUrl`, one step earlier.
    if (envelopeStatus.stripeCheckoutSessionRedirectUrl) {
      return (
        <ExternalRedirect
          url={envelopeStatus.stripeCheckoutSessionRedirectUrl}
        />
      );
    }

    // Still settling: useLazyQueryWithRetry is polling; hold the spinner.
    if (!reachMaxRetries) {
      return <Loading />;
    }

    // Timed out without a settled answer. Hand off to TnCSignedFlow, which
    // owns the unpaid outcomes (an abandoned checkout is redirected to
    // /payment there) and its own error copy — better than a dead end here.
  } else if (!envelopeStatus.shouldSendPolicyDocuments) {
    // Broker bind: TnC not signed
    if (isBrokerFlow) {
      return <CustomerAgreement invoiceIdOrGroupId={invoiceIdOrGroupId} />;
    } else {
      return (
        <div className="m-auto text-lg text-center" data-testid="TnC-message">
          <p>
            <b className="text-xl">
              Thank you for submitting your payment! Please sign the Terms &
              Conditions to proceed further.
            </b>
          </p>
          <p className="mt-4">
            If you have any questions, please contact your broker/agent.
          </p>
        </div>
      );
    }
  }

  return <TnCSignedFlow flowKind={state?.flowKind} />;
};

export const QuickComplete: React.FC = () => (
  <div className="m-auto text-lg text-center">
    <div>
      <p>
        <b data-testid="thank-you-text" className="text-xl">
          Thank you, your Policy details with Foxquilt have been updated.
        </b>
      </p>
      <p>
        If you have any questions, contact us at{' '}
        <a
          href="tel:+18888784588"
          data-testid="phone-number"
          className="underline"
        >
          1-888-878-4588
        </a>{' '}
        or{' '}
        <a
          href="mailto:support@foxquilt.com"
          data-testid="email-address"
          className="underline"
        >
          support@foxquilt.com
        </a>
        .
      </p>
    </div>
  </div>
);

export default Complete;
